import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  const cancelled = location.pathname.includes("cancel");
  const sid = params.get("session_id");

  useEffect(() => {
    if (cancelled) { setStatus("cancel"); return; }
    if (!sid) { setStatus("cancel"); return; }
    let timer;
    const poll = async () => {
      if (polls.current >= 8) { setStatus("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refresh(); setStatus("success"); return; }
        if (data.status === "failed" || data.payment_status === "expired") { setStatus("cancel"); return; }
      } catch (e) { /* keep polling */ }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, sid, refresh]);

  return (
    <div className="mm-frame mm-glow relative flex flex-col items-center justify-center px-8 text-center bg-white" data-testid="payment-result-page">
      {status === "checking" && (
        <>
          <Loader2 size={48} className="text-royal animate-spin" />
          <p className="mt-5 text-slate-500">Confirming your payment…</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 size={64} className="text-emerald-500" data-testid="payment-success-icon" />
          <h1 className="font-serif text-3xl font-bold text-navy mt-5">You're all set!</h1>
          <p className="text-slate-500 mt-2" data-testid="payment-finalizing-copy">
            Your Custom Sessions are being finalized by Coach and will be available within 24 hours or less. Your general content is ready to go right now.
          </p>
          <button data-testid="payment-home-btn" onClick={() => navigate("/home")}
            className="mt-8 w-full bg-royal text-white font-bold py-4 rounded-2xl active:scale-[0.98] transition">Back to home</button>
        </>
      )}
      {(status === "cancel" || status === "timeout") && (
        <>
          <XCircle size={64} className="text-slate-400" data-testid="payment-cancel-icon" />
          <h1 className="font-serif text-3xl font-bold text-navy mt-5">{status === "timeout" ? "Still processing" : "Checkout cancelled"}</h1>
          <p className="text-slate-500 mt-2">{status === "timeout" ? "It's taking a moment — your credits will appear shortly." : "No charge was made. You can try again anytime."}</p>
          <button data-testid="payment-retry-btn" onClick={() => navigate("/paywall")}
            className="mt-8 w-full bg-royal text-white font-bold py-4 rounded-2xl active:scale-[0.98] transition">View packs</button>
          <button onClick={() => navigate("/home")} className="mt-3 text-slate-400 text-sm">Back to home</button>
        </>
      )}
    </div>
  );
}
